/**
 * @fileoverview Card displaying order sources (OMS integrations like BaseLinker).
 * @module features/settings/components/OrderSourcesCard
 */

import { ReactElement, useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow, parseISO, subDays } from 'date-fns'
import { pl, enUS } from 'date-fns/locale'
import { Package, AlertTriangle, RefreshCw, Unplug, Plus, ChevronDown, Settings, Coins, Link2, Unlink, Link as LinkIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { motion, useSpring, useMotionValue, AnimatePresence } from 'framer-motion'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { z } from 'zod'
import type { DateRange } from 'react-day-picker'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Skeleton,
  Button,
  ConfirmPopover,
  DateRangePicker,
} from '@/shared/components/ui'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  getBaselinkerCurrency,
  setBaselinkerCurrency,
} from '../api/baselinkerCurrencyService'
import { getOrgSettings } from '../api/orgSettingsService'
import { useStoresQuery } from '../api/storeService'
import { useOmsSourcesQuery, useMapOmsSourceMutation, useUnmapOmsSourceMutation } from '../api/omsSourceService'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'
import { cn } from '@/shared/utils/cn'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import apiClient from '@/lib/apiClient'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { useIntegrationsStatus } from '../hooks/useIntegrationsStatus'
import type {
  IntegrationStatus,
  IntegrationProvider,
  IntegrationStatusType,
} from '../schemas/integrationStatusSchema'
import type { OmsSource } from '../schemas/omsSourceSchema'

/** Response schema for sync endpoints */
const syncResponseSchema = z.union([
  z.string().transform((s) => ({ message: s })),
  z.object({ message: z.string().optional() }).passthrough(),
])

/** OMS Provider types that should appear in this card */
const OMS_PROVIDERS: IntegrationProvider[] = ['BASELINKER']

/** Provider display names */
const PROVIDER_NAMES: Record<IntegrationProvider, string> = {
  FACEBOOK: 'Facebook Ads',
  GOOGLE_ADS: 'Google Ads',
  BASELINKER: 'BaseLinker',
  SHOPIFY: 'Shopify',
  BANK: 'Bank',
}

/** Provider icons */
const PROVIDER_ICONS: Record<IntegrationProvider, string> = {
  FACEBOOK: '/meta-favicon.png',
  GOOGLE_ADS: '/google-favicon.png',
  BASELINKER: '/base-favicon.png',
  SHOPIFY: '/shopify.webp',
  BANK: '',
}

/** Status badge variant configuration */
const STATUS_BADGE_VARIANTS: Record<IntegrationStatusType, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  ACTIVE: 'default',
  ERROR: 'destructive',
  EXPIRED: 'outline',
  NOT_CONFIGURED: 'secondary',
}

/** Status badge translation keys */
const STATUS_LABEL_KEYS: Record<IntegrationStatusType, string> = {
  ACTIVE: 'cards.integrations.status.active',
  ERROR: 'cards.integrations.status.error',
  EXPIRED: 'cards.integrations.status.expired',
  NOT_CONFIGURED: 'cards.integrations.status.notConfigured',
}

/**
 * Props for OmsIntegrationEntry
 */
interface OmsIntegrationEntryProps {
  integration: IntegrationStatus
  omsSources: OmsSource[]
  isExpanded: boolean
  onToggle: () => void
}

/**
 * Single OMS integration entry component.
 */
function OmsIntegrationEntry({ integration, omsSources, isExpanded, onToggle }: OmsIntegrationEntryProps): ReactElement {
  const { t, i18n } = useTranslation('features-settings')
  const [syncDateRange, setSyncDateRange] = useState<DateRange | undefined>(() => ({
    from: subDays(new Date(), 3),
    to: new Date(),
  }))
  const [selectedOmsSourceId, setSelectedOmsSourceId] = useState<string>('')
  const [selectedStoreId, setSelectedStoreId] = useState<string>('')
  const contentRef = useRef<HTMLDivElement>(null)
  const heightValue = useMotionValue(0)
  const animatedHeight = useSpring(heightValue, { stiffness: 300, damping: 30 })

  const queryClient = useQueryClient()
  const { activeOrgId } = useActiveOrg()

  // Fetch stores for mapping dropdown
  const storesQuery = useStoresQuery()
  const stores = storesQuery.data ?? []

  // OMS source mutations
  const mapOmsSourceMutation = useMapOmsSourceMutation()
  const unmapOmsSourceMutation = useUnmapOmsSourceMutation()

  // Filter OMS sources for this provider
  const providerOmsSources = omsSources.filter(
    (s) => s.omsType === integration.provider
  )
  const mappedSources = providerOmsSources.filter((s) => s.storeId !== null)
  const unmappedSources = providerOmsSources.filter((s) => s.storeId === null)

  // BaseLinker disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!activeOrgId) throw new Error('No active organization')
      const orgId = OrgIdSchema.parse(activeOrgId) as unknown as string
      await apiClient.delete(`/orgs/${orgId}/integrations/BASELINKER/credentials`)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.includes('integrations-status')
      })
    },
    onError: (error: Error) => {
      toast.error(
        error.message ||
          t('cards.integrations.baselinker.disconnectError', {
            defaultValue: 'Failed to disconnect BaseLinker',
          })
      )
    },
  })

  const dateLocale = i18n.language === 'pl' ? pl : enUS
  const statusVariant = STATUS_BADGE_VARIANTS[integration.status] ?? STATUS_BADGE_VARIANTS.NOT_CONFIGURED
  const statusLabelKey = STATUS_LABEL_KEYS[integration.status] ?? STATUS_LABEL_KEYS.NOT_CONFIGURED
  const displayName = integration.accountName ?? PROVIDER_NAMES[integration.provider]

  const lastSyncedLabel = integration.lastSyncedAt
    ? t('cards.integrations.sync.lastSynced', { time: formatDistanceToNow(parseISO(integration.lastSyncedAt), { addSuffix: true, locale: dateLocale }) })
    : t('cards.integrations.sync.neverSynced')

  const hasError = integration.status === 'ERROR' && integration.lastSyncError
  const isNotConfigured = integration.status === 'NOT_CONFIGURED'
  const isActive = integration.status === 'ACTIVE'

  // Org settings query (for allowed currencies)
  const orgSettingsQuery = useQuery({
    queryKey: activeOrgId ? ['org', activeOrgId, 'settings'] : ['settings', 'no-org'],
    queryFn: async () => {
      if (!activeOrgId) throw new Error('No active organization')
      return getOrgSettings(OrgIdSchema.parse(activeOrgId))
    },
    enabled: Boolean(activeOrgId) && isActive,
    staleTime: 5 * 60 * 1000,
  })

  // BaseLinker currency query
  const currencyQuery = useQuery({
    queryKey: ['baselinker', 'currency'],
    queryFn: getBaselinkerCurrency,
    enabled: isActive,
    staleTime: 5 * 60 * 1000,
  })

  // BaseLinker currency mutation
  const currencyMutation = useMutation({
    mutationFn: setBaselinkerCurrency,
    onSuccess: async () => {
      toast.success(t('cards.integrations.baselinker.currencySaved'))
      await queryClient.invalidateQueries({ queryKey: ['baselinker', 'currency'] })
    },
    onError: () => {
      toast.error(t('cards.integrations.baselinker.currencyError'))
    },
  })

  // Handle mapping an OMS source to a store
  const handleMapOmsSource = (): void => {
    if (!selectedOmsSourceId || !selectedStoreId) return
    mapOmsSourceMutation.mutate(
      { id: parseInt(selectedOmsSourceId, 10), storeId: parseInt(selectedStoreId, 10) },
      {
        onSuccess: () => {
          toast.success(
            t('cards.orderSources.omsSourceMapped', {
              defaultValue: 'OMS source linked to store',
            })
          )
          setSelectedOmsSourceId('')
          setSelectedStoreId('')
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to link OMS source')
        },
      }
    )
  }

  // Handle unmapping an OMS source
  const handleUnmapOmsSource = (sourceId: number): void => {
    unmapOmsSourceMutation.mutate(sourceId, {
      onSuccess: () => {
        toast.success(
          t('cards.orderSources.omsSourceUnmapped', {
            defaultValue: 'OMS source unlinked from store',
          })
        )
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to unlink OMS source')
      },
    })
  }

  const syncMutation = useMutation({
    mutationFn: async (range: DateRange) => {
      if (!range.from || !range.to) throw new Error('Date range required')
      const startDate = toDateOnlyStringLocal(range.from)
      const endDate = toDateOnlyStringLocal(range.to)
      const url = `/baselinker/sync/range?startDate=${startDate}&endDate=${endDate}`
      const res = await apiClient.post(url, {})
      return syncResponseSchema.parse(res.data)
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: activeOrgId ? ['org', activeOrgId, 'statistics'] : ['statistics'],
      })
      const msg = data.message ? ` — ${data.message}` : ''
      toast.success(`${t('cards.integrations.sync.syncTriggered', { provider: PROVIDER_NAMES[integration.provider] })}${msg}`)
    },
    onError: (err) => {
      const error = err as { response?: { status?: number; data?: unknown } }
      const status = error.response?.status
      const detail = typeof error.response?.data === 'string' ? `: ${error.response.data.slice(0, 300)}` : ''
      toast.error(`${t('cards.integrations.sync.syncFailed')}${status ? ` (HTTP ${status})` : ''}${detail}`)
    },
  })

  // Shopify linkage reconciliation mutation
  const reconcileMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/baselinker/products/reconcile-shopify-linkages', {})
      return res.data as { linksApplied: number; variantsProcessed: number; errors: string[] }
    },
    onSuccess: (data) => {
      if (data.linksApplied > 0) {
        toast.success(
          t('cards.integrations.baselinker.reconcileSuccess', {
            defaultValue: '{{count}} Shopify links applied',
            count: data.linksApplied,
          })
        )
      } else {
        toast.info(
          t('cards.integrations.baselinker.reconcileNoLinks', {
            defaultValue: 'No new Shopify links found',
          })
        )
      }
    },
    onError: (err) => {
      const error = err as { response?: { status?: number; data?: unknown } }
      toast.error(
        t('cards.integrations.baselinker.reconcileError', {
          defaultValue: 'Failed to reconcile Shopify links',
        }) + (error.response?.status ? ` (HTTP ${error.response.status})` : '')
      )
    },
  })

  useEffect(() => {
    if (contentRef.current) {
      heightValue.set(isExpanded ? contentRef.current.scrollHeight : 0)
    }
  }, [isExpanded, heightValue, syncDateRange, currencyQuery.data])

  const handleSync = (): void => {
    if (!syncDateRange?.from || !syncDateRange?.to) return
    syncMutation.mutate(syncDateRange)
  }

  return (
    <div
      className="flex flex-col rounded-lg border border-border bg-card p-4 cursor-pointer transition-[filter] hover:brightness-105"
      onClick={onToggle}
    >
      {/* Info row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <img
              src={PROVIDER_ICONS[integration.provider]}
              alt={PROVIDER_NAMES[integration.provider]}
              className="size-5"
            />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-foreground truncate">{displayName}</span>
              {!isNotConfigured && (
                <Badge
                  variant={statusVariant}
                  className={cn(
                    statusVariant === 'default' &&
                      'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30',
                    statusVariant === 'outline' &&
                      'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30'
                  )}
                >
                  {t(statusLabelKey)}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
              {integration.accountId && <span className="font-mono">{integration.accountId}</span>}
              {integration.currency && <span>{integration.currency}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Connect action for unconfigured integrations */}
          {isNotConfigured && (
            <div onClick={(e) => e.stopPropagation()}>
              <Link to="/dashboard/settings/integrations/baselinker/setup">
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Plus className="size-3.5" />
                  {t('cards.integrations.baselinker.buttons.connect', { defaultValue: 'Connect' })}
                </Button>
              </Link>
            </div>
          )}
          {/* Disconnect action */}
          {isActive && (
            <div onClick={(e) => e.stopPropagation()}>
              <ConfirmPopover
                trigger={
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    disabled={disconnectMutation.isPending}
                  >
                    <Unplug className="size-4" />
                  </Button>
                }
                title={t('cards.integrations.baselinker.disconnectTitle', {
                  defaultValue: 'Disconnect BaseLinker?',
                })}
                description={t('cards.integrations.baselinker.disconnectDescription', {
                  defaultValue:
                    'This will remove your BaseLinker integration. You can reconnect at any time.',
                })}
                confirmLabel={t('cards.integrations.baselinker.disconnectConfirm', {
                  defaultValue: 'Disconnect',
                })}
                onConfirm={() => disconnectMutation.mutateAsync()}
              />
            </div>
          )}
          <ChevronDown
            className={cn(
              'size-4 text-muted-foreground transition-transform duration-200',
              isExpanded && 'rotate-180'
            )}
          />
        </div>
      </div>

      {/* Animated expanded content */}
      <motion.div
        style={{ height: animatedHeight }}
        className="overflow-hidden"
      >
        <div ref={contentRef}>
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="pt-3 space-y-3"
              >
                {/* Error message */}
                {hasError && (
                  <div className="flex items-start gap-1.5 text-xs text-destructive">
                    <AlertTriangle className="size-3 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{integration.lastSyncError}</span>
                  </div>
                )}

                {/* Sync info row */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                  <span className="text-xs text-muted-foreground">{lastSyncedLabel}</span>
                  {integration.lastSyncRecordsCount != null && (
                    <span className="text-xs text-muted-foreground">
                      {t('cards.integrations.sync.records', { count: integration.lastSyncRecordsCount })}
                    </span>
                  )}
                </div>

                {/* Sync actions */}
                {isActive && (
                  <div
                    className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t border-border"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DateRangePicker
                      value={syncDateRange}
                      onChange={setSyncDateRange}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSync}
                      disabled={syncMutation.isPending || !syncDateRange?.from || !syncDateRange?.to}
                      className="gap-1.5 shrink-0"
                    >
                      <RefreshCw className={cn('size-3.5', syncMutation.isPending && 'animate-spin')} />
                      {syncMutation.isPending ? t('cards.integrations.sync.syncing') : t('cards.integrations.sync.syncButton')}
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/dashboard/settings/status-mappings">
                        <Settings className="size-4 mr-1.5" />
                        {t('cards.integrations.baselinker.editStatuses')}
                      </Link>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => reconcileMutation.mutate()}
                      disabled={reconcileMutation.isPending}
                      className="gap-1.5 shrink-0"
                      title={t('cards.integrations.baselinker.reconcileTooltip', {
                        defaultValue: 'Link Baselinker products to Shopify store variants',
                      })}
                    >
                      <LinkIcon className={cn('size-3.5', reconcileMutation.isPending && 'animate-pulse')} />
                      {reconcileMutation.isPending
                        ? t('cards.integrations.baselinker.reconciling', { defaultValue: 'Linking...' })
                        : t('cards.integrations.baselinker.reconcileShopify', { defaultValue: 'Link Shopify' })}
                    </Button>
                  </div>
                )}

                {/* Currency selector */}
                {isActive && (
                  <div
                    className="flex items-center justify-between gap-3 pt-2 border-t border-border"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Coins className="size-4" />
                      <span>{t('cards.integrations.baselinker.accountCurrency')}</span>
                    </div>
                    {currencyQuery.isLoading || orgSettingsQuery.isLoading ? (
                      <Skeleton className="h-8 w-20" />
                    ) : (
                      <Select
                        value={currencyQuery.data?.currency ?? ''}
                        onValueChange={(value) => currencyMutation.mutate(value)}
                        disabled={currencyMutation.isPending}
                      >
                        <SelectTrigger className="w-20 h-8">
                          <SelectValue placeholder={t('cards.integrations.baselinker.selectCurrency')} />
                        </SelectTrigger>
                        <SelectContent>
                          {(orgSettingsQuery.data?.allowedReportingCurrencies ?? []).map((code) => (
                            <SelectItem key={code} value={code}>
                              {code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {/* OMS Source Mapping */}
                {isActive && providerOmsSources.length > 0 && (
                  <div
                    className="pt-3 border-t border-border space-y-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                      <Link2 className="size-4" />
                      <span>
                        {t('cards.orderSources.sourceMapping', {
                          defaultValue: 'Source → Store Mapping',
                        })}
                      </span>
                    </div>

                    {/* Mapped sources */}
                    {mappedSources.map((source) => (
                      <div
                        key={source.id}
                        className="flex items-center justify-between gap-2 bg-muted/50 rounded-md px-3 py-2"
                      >
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="text-sm font-medium truncate">
                            {source.omsSourceName || source.omsSourceId}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            → {source.storeName || t('cards.orderSources.unknownStore', { defaultValue: 'Unknown store' })}
                            {' • '}
                            {source.totalOrdersCount.toLocaleString()} {t('cards.orderSources.orders', { defaultValue: 'orders' })}
                          </span>
                        </div>
                        <ConfirmPopover
                          trigger={
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="shrink-0 text-muted-foreground hover:text-destructive"
                              disabled={unmapOmsSourceMutation.isPending}
                            >
                              <Unlink className="size-4" />
                            </Button>
                          }
                          title={t('cards.orderSources.unlinkTitle', {
                            defaultValue: 'Unlink OMS Source?',
                          })}
                          description={t('cards.orderSources.unlinkDescription', {
                            defaultValue:
                              'Orders from this source will no longer be attributed to the store.',
                          })}
                          confirmLabel={t('cards.orderSources.unlinkConfirm', {
                            defaultValue: 'Unlink',
                          })}
                          onConfirm={() => handleUnmapOmsSource(source.id)}
                        />
                      </div>
                    ))}

                    {/* Unmapped sources - mapping UI */}
                    {unmappedSources.length > 0 && stores.length > 0 && (
                      <div className="flex gap-2">
                        <Select
                          value={selectedOmsSourceId}
                          onValueChange={setSelectedOmsSourceId}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue
                              placeholder={t('cards.orderSources.selectSource', {
                                defaultValue: 'Select source...',
                              })}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {unmappedSources.map((source) => (
                              <SelectItem key={source.id} value={String(source.id)}>
                                {source.omsSourceName || source.omsSourceId}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={selectedStoreId}
                          onValueChange={setSelectedStoreId}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue
                              placeholder={t('cards.orderSources.selectStore', {
                                defaultValue: 'Select store...',
                              })}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {stores.map((store) => (
                              <SelectItem key={store.id} value={String(store.id)}>
                                {store.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleMapOmsSource}
                          disabled={
                            !selectedOmsSourceId ||
                            !selectedStoreId ||
                            mapOmsSourceMutation.isPending
                          }
                        >
                          <Link2 className="size-4" />
                        </Button>
                      </div>
                    )}

                    {/* No stores available */}
                    {unmappedSources.length > 0 && stores.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        {t('cards.orderSources.noStoresAvailable', {
                          defaultValue: 'Connect a store first to map OMS sources.',
                        })}
                      </p>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  )
}

/**
 * OrderSourcesCard
 *
 * Displays order sources: OMS integrations (BaseLinker).
 * OMS source → Store mapping is managed within this card.
 *
 * @component
 */
export function OrderSourcesCard(): ReactElement {
  const { t } = useTranslation('features-settings')
  const integrationsQuery = useIntegrationsStatus()
  const omsSourcesQuery = useOmsSourcesQuery()
  const cardRef = useRef<HTMLDivElement>(null)

  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Filter to only OMS providers (BaseLinker)
  const omsIntegrations = (integrationsQuery.data ?? []).filter(
    (integration) => OMS_PROVIDERS.includes(integration.provider)
  )

  // Get OMS sources
  const omsSources = omsSourcesQuery.data ?? []

  // Check if there are any order sources
  const hasAnyOrderSources = omsIntegrations.length > 0

  const handleToggle = (id: string): void => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  // Collapse all when clicking outside the card
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      if (cardRef.current?.contains(target)) return
      const popoverContent = (target as Element).closest?.('[data-radix-popper-content-wrapper]')
      if (popoverContent) return
      setExpandedId(null)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isLoading = integrationsQuery.isLoading || omsSourcesQuery.isLoading
  const isError = integrationsQuery.isError || omsSourcesQuery.isError

  return (
    <Card ref={cardRef} className="glass-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="size-5" />
            {t('cards.orderSources.title', { defaultValue: 'Order Sources' })}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            <div className="flex items-center gap-4 p-4 rounded-lg border border-border">
              <Skeleton className="size-10 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
          </div>
        ) : isError ? (
          <div className="text-center py-8 text-destructive">
            {t('cards.integrations.loadError')}
          </div>
        ) : !hasAnyOrderSources ? (
          <div className="text-center py-8">
            <Package className="size-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground mb-4">
              {t('cards.orderSources.noSources', { defaultValue: 'No order sources connected' })}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {t('cards.orderSources.connectHint', {
                defaultValue: 'Connect BaseLinker to import orders from your stores.',
              })}
            </p>
            <Link to="/dashboard/settings/integrations/baselinker/setup">
              <Button variant="outline" size="sm" className="gap-1.5">
                <Plus className="size-3.5" />
                {t('cards.orderSources.connectBaseLinker', { defaultValue: 'Connect BaseLinker' })}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {/* BaseLinker integrations */}
            {omsIntegrations.map((integration, idx) => {
              const entryId = `${integration.provider}-${integration.connectionId ?? idx}`
              return (
                <OmsIntegrationEntry
                  key={entryId}
                  integration={integration}
                  omsSources={omsSources}
                  isExpanded={expandedId === entryId}
                  onToggle={() => handleToggle(entryId)}
                />
              )
            })}

            {/* Show BaseLinker connect if not connected */}
            {omsIntegrations.length === 0 && (
              <div className="pt-2 border-t border-border">
                <Link to="/dashboard/settings/integrations/baselinker/setup">
                  <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
                    <Plus className="size-3.5" />
                    {t('cards.orderSources.addBaseLinker', { defaultValue: 'Add BaseLinker' })}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default OrderSourcesCard
